/**
 * Multi-series line chart, drawn as inline SVG. No charting library: this is a
 * few dozen lines of path maths, and a dependency here would cost more bytes
 * than the whole client bundle saves.
 *
 * Colour rules that are not negotiable:
 *  - Series hues are a validated categorical set (worst adjacent CVD ΔE 9.1),
 *    assigned in fixed order by series index. They are never the brand pink,
 *    and a filter that removes a series must not repaint the survivors.
 *  - Two of those hues fall below 3:1 against the light surface, so identity is
 *    never carried by colour alone: the coloured series are direct-labelled at
 *    their last point, every series is in the legend, and all of them are in the
 *    table view below.
 *  - One y-axis. Two measures of different scale would need two charts.
 *
 * EVERY series is drawn, however many there are - the point of this screen is to
 * see the whole estate at once. What does not scale past four is COLOUR: a fifth
 * generated hue is indistinguishable from an existing one under colour blindness,
 * so the tail is drawn in the de-emphasis grey instead of being given hues it
 * cannot carry, or being hidden behind an 「その他」 line that answers nothing.
 *
 * The reader gets the tail back through the crosshair: hovering a month lists
 * every series and its value for that month, biggest first.
 */
import { useId, useState } from 'react';

export interface Series {
  /** Stable identity — colour follows this, never the row's rank. */
  id: string;
  label: string;
  values: number[];
}

const SERIES_COLORS = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
];

const WIDTH = 720;
const HEIGHT = 260;
const PAD = { top: 16, right: 92, bottom: 28, left: 44 };

/**
 * Colour by fixed slot, and grey past the fourth.
 *
 * Emphasis, not omission: the fifth line onwards is still drawn, still hovered
 * and still in the legend and the table - it just does not claim a hue the
 * palette cannot separate.
 */
const colorFor = (index: number) => SERIES_COLORS[index] ?? 'var(--ink-300)';

/** Series that get a direct end-label. Past four, the labels collide. */
const LABELLED = SERIES_COLORS.length;

/** Rows in the tooltip before the rest is summarised. */
const TOOLTIP_ROWS = 12;

/**
 * A round number at or above the largest value, for the top of the axis.
 *
 * The `<= 5` shortcut exists so a chart of small counts gets 0..5 rather than
 * ticks that round to the same integer twice. It cannot apply below 1: a spend
 * chart whose largest month is $0.10 was given an axis of 0..5 and drew every
 * line flat along the bottom - technically true, and unreadable.
 */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  if (value >= 1 && value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
}

export function LineChart({
  labels,
  series,
  unit = '件',
  format,
}: {
  labels: string[];
  series: Series[];
  unit?: string;
  /**
   * How a value is written, when the unit is not a suffix.
   *
   * Money is the case: "$0.10" is not "0.1" plus a unit, and a chart that says
   * 0.1 on its axis while every tile beside it says $0.10 makes the reader do
   * the conversion. The screen owns the formatting; this just uses it.
   */
  format?: (value: number) => string;
}) {
  const titleId = useId();
  const [hover, setHover] = useState<number | null>(null);

  /*
   * The largest value as it is, not floored at 1. Flooring made every fractional
   * chart - spend, in dollars - report a maximum of 1 and then round it up to an
   * axis of 0..5, flattening $0.10 of real movement onto the baseline. niceMax
   * handles the empty case.
   */
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const x = (index: number) =>
    PAD.left + (labels.length <= 1 ? plotWidth / 2 : (index / (labels.length - 1)) * plotWidth);
  const y = (value: number) => PAD.top + plotHeight - (value / max) * plotHeight;

  const write = format ?? ((value: number) => `${value.toLocaleString('ja-JP')}`);

  /*
   * Which series get an end-label: the coloured ones, and only where the label
   * would not sit on top of one already placed.
   *
   * Series that end at the same value - five stores at zero - put every label on
   * the same baseline, and they overprint into a smear. Nudging them apart
   * detaches each label from its line, so the ones that collide simply do not
   * get one: the legend and the crosshair already carry identity, which is what
   * they are for.
   */
  const labelled = new Set<number>();
  const placed: number[] = [];
  series.forEach((one, index) => {
    if (index >= LABELLED) return;
    const at = y(one.values.at(-1) ?? 0);
    if (placed.some((other) => Math.abs(other - at) < 12)) return;
    placed.push(at);
    labelled.add(index);
  });
  /*
   * Tick VALUES stay exact and only the label is rounded - rounding the value
   * would move the gridline away from the number printed on it.
   */
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => max * fraction);

  /*
   * What the crosshair says: every series at the hovered month, biggest first.
   *
   * Sorted by value rather than by the series order, because at a given month
   * the question is "who was highest", not "who is fourth in the legend". Past a
   * dozen rows the tooltip stops being readable, so the tail is summed into one
   * line - the numbers themselves are all in the table below.
   */
  const reading = (() => {
    if (hover === null) return { shown: [], rest: 0, restTotal: 0 };
    const rows = series
      .map((one, index) => ({ id: one.id, label: one.label, index, value: one.values[hover] ?? 0 }))
      .sort((a, b) => b.value - a.value);
    const shown = rows.slice(0, TOOLTIP_ROWS);
    const rest = rows.slice(TOOLTIP_ROWS);
    return {
      shown,
      rest: rest.length,
      restTotal: rest.reduce((sum, row) => sum + row.value, 0),
    };
  })();

  return (
    <div className="chart">
      <div className="chart__legend">
        {series.map((s, index) => (
          <span key={s.id}>
            <span className="chart__swatch" style={{ background: colorFor(index) }} />
            {s.label}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        onMouseLeave={() => setHover(null)}
      >
        <title id={titleId}>
          月別の記事作成数（{series.map((s) => s.label).join('、')}）
        </title>

        {/* Recessive grid: present enough to read a value, quiet enough to ignore. */}
        <g className="chart__grid">
          {ticks.map((tick) => (
            <line key={tick} x1={PAD.left} x2={WIDTH - PAD.right} y1={y(tick)} y2={y(tick)} />
          ))}
        </g>
        <g className="chart__axis">
          {ticks.map((tick) => (
            <text key={tick} x={PAD.left - 8} y={y(tick) + 4} textAnchor="end">
              {write(tick)}
            </text>
          ))}
          {labels.map((label, index) => (
            <text key={label} x={x(index)} y={HEIGHT - 8} textAnchor="middle">
              {label}
            </text>
          ))}
        </g>

        {series.map((s, seriesIndex) => (
          <g key={s.id}>
            <path
              d={s.values
                .map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(value)}`)
                .join(' ')}
              fill="none"
              stroke={colorFor(seriesIndex)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              // The grey tail recedes rather than competing with the four that
              // carry identity. Still fully drawn, still hovered.
              opacity={seriesIndex < LABELLED ? 1 : 0.55}
            />
            {s.values.map((value, index) => (
              <circle
                key={`${s.id}-${index}`}
                cx={x(index)}
                cy={y(value)}
                r={hover === index ? 5 : seriesIndex < LABELLED ? 3.5 : 2.5}
                fill={colorFor(seriesIndex)}
                // A 2px surface ring keeps overlapping marks separable.
                stroke="var(--surface)"
                strokeWidth={2}
                opacity={seriesIndex < LABELLED ? 1 : 0.55}
              />
            ))}
            {/*
              A direct label at the last point, for the coloured series only.
              Past four they converge at the right edge and stacking them
              detaches each label from its line - the legend and the crosshair
              carry the rest, which is what they are for.
            */}
            {labelled.has(seriesIndex) && (
              <text
                x={WIDTH - PAD.right + 8}
                y={y(s.values.at(-1) ?? 0) + 4}
                className="chart__axis"
                style={{ fill: 'var(--ink-700)' }}
              >
                {s.label}
              </text>
            )}
          </g>
        ))}

        {/* Crosshair + generous hit targets, one per x position. */}
        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
            stroke="var(--brand-300)"
            strokeWidth={1}
          />
        )}
        {labels.map((label, index) => (
          <rect
            key={`hit-${label}`}
            x={x(index) - plotWidth / Math.max(labels.length, 1) / 2}
            y={PAD.top}
            width={plotWidth / Math.max(labels.length, 1)}
            height={plotHeight}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
          />
        ))}
      </svg>

      {/*
        The crosshair reading: every series at this month, biggest first.
        
        This is where the grey tail becomes readable again. A line the eye cannot
        pick out of thirty is still a row here, with its name, its swatch and its
        number - which is the whole reason the tail is drawn rather than folded
        into an 「その他」 line nobody can act on.
      */}
      {hover !== null && (
        <div
          className="chart__tooltip"
          style={{
            left: `${(x(hover) / WIDTH) * 100}%`,
            top: 24,
            transform: x(hover) > WIDTH / 2 ? 'translateX(-108%)' : 'translateX(8px)',
          }}
        >
          <strong>{labels[hover]}</strong>
          <dl>
            {reading.shown.map((row) => (
              <div key={row.id} style={{ display: 'contents' }}>
                <dt>
                  <span className="chart__swatch" style={{ background: colorFor(row.index) }} />
                  {row.label}
                </dt>
                <dd>
                  {write(row.value)}
                  {format ? '' : unit}
                </dd>
              </div>
            ))}
            {reading.rest > 0 && (
              <div style={{ display: 'contents' }}>
                <dt className="muted">ほか {reading.rest} 件</dt>
                <dd className="muted">
                  {write(reading.restTotal)}
                  {format ? '' : unit}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

/**
 * One series, small enough to sit in a table cell.
 *
 * A sparkline is a shape, not a chart: no axes, no labels, no hover. It answers
 * "which way is this going" in a row that is already carrying the numbers, which
 * is exactly the job a full chart per row would do badly.
 *
 * Its own scale, deliberately. A row's shape is about that row's own movement;
 * scaling every sparkline to the biggest clinic would flatten all the others
 * into a straight line and say nothing. The magnitudes are in the columns beside
 * it, which is where comparison belongs.
 */
export function Sparkline({ values, label }: { values: number[]; label: string }) {
  const width = 72;
  const height = 22;
  if (values.length < 2) return <span className="muted small">—</span>;

  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const y = (value: number) => height - 3 - (value / max) * (height - 6);
  const path = values.map((value, at) => `${at === 0 ? 'M' : 'L'}${at * step},${y(value)}`).join(' ');
  const last = values.at(-1) ?? 0;

  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={path} fill="none" stroke="var(--series-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {/* The end point, ringed in the surface so it stays visible over the line. */}
      <circle cx={(values.length - 1) * step} cy={y(last)} r={2.5} fill="var(--series-1)" stroke="var(--surface)" strokeWidth={2} />
    </svg>
  );
}

/** The table view. Required here: two series hues sit under 3:1 on this surface. */
export function SeriesTable({
  labels,
  series,
  rowHeader,
}: {
  labels: string[];
  series: Series[];
  rowHeader: string;
}) {
  return (
    <div className="table-wrap">
      <table>
        <caption>グラフと同じ数値です。スクリーンリーダーと印刷用。</caption>
        <thead>
          <tr>
            <th scope="col">{rowHeader}</th>
            {labels.map((label) => (
              <th key={label} scope="col" className="num">
                {label}
              </th>
            ))}
            <th scope="col" className="num">
              合計
            </th>
          </tr>
        </thead>
        <tbody>
          {series.map((s) => (
            <tr key={s.id}>
              <th scope="row">{s.label}</th>
              {s.values.map((value, index) => (
                <td key={`${s.id}-${index}`} className="num">
                  {value}
                </td>
              ))}
              <td className="num">
                <strong>{s.values.reduce((total, value) => total + value, 0)}</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * CSV of exactly what is on screen. Excel on a Japanese Windows opens UTF-8 as
 * Shift_JIS unless the file starts with a BOM, so the BOM is not optional here.
 */
export function downloadCsv(filename: string, labels: string[], series: Series[]): void {
  const escape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`;
  const rows = [
    ['名称', ...labels, '合計'].map(escape).join(','),
    ...series.map((s) =>
      [s.label, ...s.values, s.values.reduce((total, value) => total + value, 0)]
        .map(escape)
        .join(','),
    ),
  ];
  // Written as an escape, not a literal BOM character in the source file.
  const blob = new Blob([`\ufeff${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
